<template>
  <div class="upload-container">
    <div v-if="uploadProgress === -1 && !showTimeline" class="drop-file-area">
      <div
        class="content-area"
        @dragover.prevent="handleDragOver"
        @dragenter.prevent="handleDragEnter"
        @dragleave.prevent="handleDragLeave"
        @drop.prevent="handleFileDrop"
        :class="{ highlight: isDraggingOver }"
      >
        <div>{{ $t('animation.upload.dropVideo') }}</div>
        <p class="caption">{{ $t('animation.upload.maxSize') }}</p>

        <SecondBtn @click="triggerFileInput">{{ $t('animation.upload.selectFile') }}</SecondBtn>

        <input
          type="file"
          ref="fileInput"
          @change="handleFileChange"
          style="display: none"
          accept=".mp4,.avi,.mov,video/mp4,video/x-msvideo,video/quicktime"
        />
      </div>
    </div>

    <div class="content-wrapper" v-else-if="uploadProgress === -1 && showTimeline">
      <TimeLineEditor
        :videoFile="selectedFile"
        @cancel="resetState"
        @submit-trim="uploadFileWithTrim"
      />
    </div>

    <ProgressSpinner
      v-else
      :progress="uploadProgress"
      :loaded-size="loadedSize"
      :total-size="totalSize"
      :show-size="true"
      :is-processing="isProcessing && !showDebugProcessingProgress"
      :label="$t('animation.upload.uploading')"
      :subtitle="isProcessing ? $t('animation.upload.processing') : $t('animation.upload.uploadingToServer')"
    />
  </div>

  <!-- Notification Toast -->
  <NotificationToast
    :show="showNotification"
    :message="notificationMessage"
    :is-error="isErrorNotification"
    @close="closeNotification"
  />
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import SecondBtn from "@/components/BaseComponents/SecondBtn.vue";
import TimeLineEditor from "@/components/TimeLineEditor.vue";
import ProgressSpinner from "@/components/BaseComponents/ProgressSpinner.vue";
import NotificationToast from "@/components/BaseComponents/NotificationToast.vue";
import { animationProcessingAPI } from "@/services/api";
import { preloadCharacterModel } from "@/utils/characterModel";
import { trackEvent, trackFormSubmission, trackError } from "@/utils/analytics";
import { useI18n } from 'vue-i18n';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const uploadProgress = ref(-1);
const loadedSize = ref(0);
const totalSize = ref(0);
const isUploading = ref(false);
const isDraggingOver = ref(false);
const fileInput = ref(null);

const showTimeline = ref(false);
const selectedFile = ref(null);

const isProcessing = ref(false);
const showDebugProcessingProgress = ref(false);

// Notification state
const showNotification = ref(false);
const notificationMessage = ref("");
const isErrorNotification = ref(false);
let isUnmounted = false;

onBeforeUnmount(() => {
  isUnmounted = true;
});

function applyDebugPreview() {
  if (!store.getters.isDebugMode) return;

  const mode = route.query.debug;
  const progress = Number(route.query.progress);

  if (mode === "uploading") {
    uploadProgress.value = Number.isFinite(progress) ? progress : 42;
    loadedSize.value = 22 * 1024 * 1024;
    totalSize.value = 50 * 1024 * 1024;
    isProcessing.value = false;
    showDebugProcessingProgress.value = false;
  }

  if (mode === "processing") {
    uploadProgress.value = Number.isFinite(progress) ? progress : 42;
    loadedSize.value = 0;
    totalSize.value = 0;
    isProcessing.value = !Number.isFinite(progress);
    showDebugProcessingProgress.value = Number.isFinite(progress);
  }
}

function showErrorNotification(message) {
  notificationMessage.value = message;
  isErrorNotification.value = true;
  showNotification.value = true;
}

function closeNotification() {
  showNotification.value = false;
}

function handleDragOver(e) {
  e.preventDefault();
}
function handleDragEnter(e) {
  e.preventDefault();
  isDraggingOver.value = true;
}
function handleDragLeave(e) {
  e.preventDefault();
  isDraggingOver.value = false;
}
function triggerFileInput() {
  fileInput.value.click();
}
function handleFileChange(e) {
  const files = e.target.files;
  if (files.length) validateAndPrepareFile(files[0]);
}
function handleFileDrop(e) {
  e.preventDefault();
  const files = e.dataTransfer.files;
  if (files.length) validateAndPrepareFile(files[0]);
}

function validateAndPrepareFile(file) {
  const validTypes = ["video/mp4", "video/x-msvideo", "video/quicktime"];
  const validExts = [".mp4", ".avi", ".mov"];

  const lower = file.name.toLowerCase();
  const isValidType = validTypes.includes(file.type);
  const isValidExt = validExts.some((ext) => lower.endsWith(ext));

  if ((isValidType || isValidExt) && file.size <= 52428800) {
    selectedFile.value = file;
    showTimeline.value = true;
    isDraggingOver.value = false;

    trackEvent("video_file_selected", {
      file_type: file.type,
      file_size_mb: Number(file.size / (1024 * 1024)).toFixed(2),
      file_extension: file.name.split(".").pop()?.toLowerCase() || "",
      user_plan: store.state.activePlan,
    });
  } else {
    isDraggingOver.value = false;
    showErrorNotification(t('animation.upload.invalidFile'));

    trackEvent("video_file_validation_failed", {
      file_type: file.type,
      file_size_mb: Number(file.size / (1024 * 1024)).toFixed(2),
      reason: file.size > 52428800 ? "file_too_large" : "invalid_format",
    });
  }
}

async function uploadFileWithTrim({
  start_time,
  end_time,
  foot_lock,
  hands,
  face,
  multi_person,
  name,
}) {
  if (!selectedFile.value) return;

  isUploading.value = true;
  uploadProgress.value = 0;
  loadedSize.value = 0;
  totalSize.value = selectedFile.value.size;

  const formData = new FormData();
  formData.append("video_file", selectedFile.value);
  formData.append("start_seconds", start_time);
  formData.append("end_seconds", end_time);
  formData.append("foot_lock", foot_lock);
  formData.append("hands", hands);
  formData.append("face", face);
  formData.append("multi_person", multi_person);
  formData.append("name", name);

  const sessionId = store.getters.getSessionId;

  trackEvent("video_upload_started", {
    method: "upload",
    file_size_mb: Number(selectedFile.value.size / (1024 * 1024)).toFixed(2),
    duration_trimmed: end_time - start_time,
    settings: {
      foot_lock,
      hands,
      face,
      multi_person,
    },
    user_plan: store.state.activePlan,
  });

  try {
    const response = await animationProcessingAPI.uploadVideo(
      formData,
      sessionId,
      (loaded, total) => {
        if (isUnmounted) return;
        loadedSize.value = loaded;
        totalSize.value = total;
        uploadProgress.value = (loaded / total) * 100;
      }
    );
    void preloadCharacterModel();
    if (isUnmounted) return;

    if (response.hashed_id && response.progress_id) {
      window.sessionStorage.setItem(
        `pending-mocap-progress:${response.hashed_id}`,
        response.progress_id
      );
    }

    trackEvent("video_upload_success", {
      animation_id: response.hashed_id,
      progress_id: response.progress_id,
      method: "upload",
      file_size_mb: Number(selectedFile.value.size / (1024 * 1024)).toFixed(2),
      duration_trimmed: end_time - start_time,
    });
    trackFormSubmission("video_upload_form", true);

    isUploading.value = false;
    router.push({
      path: "/animation",
      query: {
        animationId: response.hashed_id,
        ...(response.progress_id ? { taskId: response.progress_id } : {}),
      },
    });
  } catch (error) {
    if (isUnmounted) return;
    showErrorNotification(t('animation.upload.failed'));
    trackEvent("video_upload_failed", {
      method: "upload",
      file_size_mb: Number(selectedFile.value.size / (1024 * 1024)).toFixed(2),
    });
    trackFormSubmission("video_upload_form", false);
    trackError("video_upload_error", error.message || "Unknown error", "video_upload_form");
    resetState();
  }
}

function resetState() {
  uploadProgress.value = -1;
  loadedSize.value = 0;
  totalSize.value = 0;
  isUploading.value = false;
  isDraggingOver.value = false;
  showTimeline.value = false;
  selectedFile.value = null;
  showDebugProcessingProgress.value = false;
}

onMounted(() => {
  applyDebugPreview();
});
</script>

<style scoped>
.upload-container {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 50vh;
  justify-content: center;
  align-items: center;
  display: flex;
}

.drop-file-area {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40%;
  height: 30vh;
  border-radius: var(--border-radius-lg);
  background-color: var(--bg-card);
  padding: var(--spacing-notification);
  text-align: center;
  margin: var(--spacing-notification);
  box-sizing: border-box;
  transition: var(--transition-normal);
}

.content-area {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 90%;
  height: 70%;
  border: 2px dashed var(--bg-card);
  padding: var(--spacing-notification);
  border-radius: var(--border-radius-md);
}

.content-wrapper {
  border-radius: var(--border-radius-lg);
  background-color: var(--bg-card);
  width: 70%;
}

.caption {
  color: var(--color-white-40);
}

.highlight {
  border-color: grey;
  background-color: var(--color-white-20);
}

.progress-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.progress-placeholder {
  background-color: var(--bg-card);
  border-radius: var(--border-radius-lg);
  padding: var(--gap-lg);
}

.progress-circle-bg,
.progress-circle-fg {
  transition: var(--transition-normal);
}

.header-2 {
  margin-bottom: var(--spacing-xl);
  font-size: var(--font-size-h3);
}
</style>
